import React from 'react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import MenuItem from '@material-ui/core/MenuItem';
import readXlsxFile from 'read-excel-file';

import {
  DataTable,
  toast,
  Button as DsButton,
  PageTitle,
  FilterSelect,
  Tag,
  Modal,
  criarCopiaDeColuna
} from '../../components/ds';
import { Utils } from '../../services';
import AppState from '../../state';
import { browserHistory } from 'react-router';
import visaoAPI from '../../api/visaoPessoas';
// Endpoints por pessoa, os mesmos que a ficha do funcionário usa. A janela de
// detalhe reaproveita em vez de criar endpoint novo: é exatamente o mesmo dado,
// só que exibido em outro lugar.
import * as funcionarioAPI from '../../api/funcionarios';
// Página de vale-transporte dentro da aba Benefícios (pedido do mês).
import ValeTransporte from './ValeTransporte';
import ValeAlimentacao from './ValeAlimentacao';

// Visão consolidada de pessoas: responde "onde a empresa está exposta", que é
// uma pergunta diferente da ficha do funcionário ("qual a situação desta
// pessoa"). Por isso a tela é de leitura — quem corrige o dado de alguém
// continua indo no cadastro, onde tem o contexto todo.
//
// A exceção é a importação em massa, que mora aqui de propósito: ela não é
// edição registro a registro, é carga do histórico que hoje só existe em
// planilha no DP. E é justamente aqui que dá pra ver o buraco antes ("faltam N
// pessoas sem férias lançadas") e conferir o resultado logo depois, na mesma
// tela.

// Colunas dos modelos de planilha. São os mesmos nomes que os endpoints de
// importação esperam, e é por eles que a importação confere o cabeçalho.
//
// Os modelos em si são arquivos prontos em server/arquivos (com cores, aba
// de instruções e listas). Se uma coluna mudar aqui, o .xlsx precisa ser
// refeito com os mesmos nomes na linha 1 — senão a importação recusa a
// planilha dizendo qual coluna falta.
const MODELOS = {
  ferias: {
    arquivo: 'modelo-importacao-ferias.xlsx',
    colunas: [
      'cpf_funcionario',
      'periodo_aquisitivo_inicio',
      'periodo_aquisitivo_fim',
      // Período concessivo não vem da planilha: são sempre os 12 meses
      // seguintes ao aquisitivo, então o servidor calcula.
      'periodo_gozado_inicio',
      'periodo_gozado_fim',
      // dias_gozados e dias_saldo vêm com fórmula no modelo (.xlsx), só para
      // adiantar quem preenche — aqui chegam como número normal.
      'dias_gozados',
      'dias_saldo',
      'abono_pecuniario'
    ]
  },
  salarios: {
    arquivo: 'modelo-importacao-salarios.xlsx',
    colunas: [
      'cpf_funcionario',
      'salario',
      'ajuda_custo',
      'complemento',
      'dt_vigencia_inicio',
      'dt_vigencia_fim',
      'motivo'
    ]
  },
  // Saldo do extrato do FGTS (Caixa) numa data. Substitui o saldo anterior da
  // pessoa: é uma fotografia nova, não um valor a somar.
  fgts: {
    arquivo: 'modelo-importacao-fgts.xlsx',
    colunas: ['cpf_funcionario', 'saldo_fgts', 'dt_saldo']
  }
};

// Endpoint de importação de cada modelo. Prévia e gravação usam o mesmo, com
// validarApenas = true na prévia.
const IMPORTADOR = {
  ferias: (linhas, validarApenas) => visaoAPI.importarFerias(linhas, validarApenas),
  salarios: (linhas, validarApenas) => visaoAPI.importarSalarios(linhas, validarApenas),
  fgts: (linhas, validarApenas) => visaoAPI.importarFgts(linhas, validarApenas)
};

// Mesmos rótulos usados na ficha do funcionário, para as duas telas não
// chamarem o mesmo motivo por nomes diferentes.
const ROTULO_MOTIVO = {
  justa_causa: 'Justa causa',
  sem_justa_causa: 'Sem justa causa',
  pedido: 'Pedido de demissão',
  acordo: 'Acordo entre as partes',
  morte: 'Morte',
  aposentadoria: 'Aposentadoria',
  fim_contrato: 'Fim de contrato',
  culpa_reciproca: 'Culpa recíproca'
};

const ROTULO_AVISO = {
  trabalhado: 'Trabalhado',
  indenizado: 'Indenizado'
};

const rotulo = (mapa, valor) => (valor ? mapa[valor] || valor : '—');

// Frase que explica, na janela, por que ela mostra só uma parte da ficha.
const TITULO_DETALHE = {
  ferias: 'Histórico de férias desta pessoa.',
  decimo: 'Histórico de 13º salário desta pessoa.',
  salarios: 'Histórico de salário desta pessoa (a composição do mês está na lista).',
  rescisao: 'Dados do desligamento desta pessoa.',
  fgts: 'Saldo de FGTS desta pessoa: o informado pelo extrato mais os depósitos lançados depois dele.'
};

const TOM_SITUACAO = {
  vencido: 'red',
  a_vencer: 'orange',
  em_dia: 'green',
  sem_registro: 'grey',
  // Cinza de propósito: quem saiu não é urgência de férias. Um vermelho de
  // "vencido" em quem já foi embora só rouba atenção de quem ainda está na
  // empresa e precisa marcar.
  desligado: 'grey'
};

const ROTULO_SITUACAO = {
  vencido: 'Vencido',
  a_vencer: 'A vencer',
  em_dia: 'Em dia',
  sem_registro: 'Sem registro',
  desligado: 'Desligado'
};

const dinheiro = (v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const data = (v) => (v ? Utils.dateToPT(String(v)) : '—');

// 2026-08 -> 08/2026. Competencia o pessoal le como mes/ano, nao como data.
const rotuloCompetencia = (v) => {
  if (!v) return '—';
  const partes = String(v).split('-');
  return partes.length >= 2 ? `${partes[1]}/${partes[0]}` : String(v);
};

const numeroOuTraco = (v) =>
  v === null || v === undefined || v === '' ? '—' : v;

// O Excel devolve célula de data como objeto Date; o restante vem como texto.
// Os endpoints só aceitam AAAA-MM-DD, então a normalização acontece aqui, uma
// vez, em vez de espalhada por cada campo.
const paraISO = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '';
  if (valor instanceof Date) {
    const mes = String(valor.getMonth() + 1).padStart(2, '0');
    const dia = String(valor.getDate()).padStart(2, '0');
    return `${valor.getFullYear()}-${mes}-${dia}`;
  }
  // Número de série do Excel (dias desde 30/12/1899): é como a data chega
  // quando a célula perdeu o formato de data (ex.: planilha salva pelo
  // LibreOffice).
  if (typeof valor === 'number' && valor > 0) {
    const d = new Date(Math.round((valor - 25569) * 86400000));
    const mes = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dia = String(d.getUTCDate()).padStart(2, '0');
    return `${d.getUTCFullYear()}-${mes}-${dia}`;
  }
  const texto = String(valor).trim();
  // dd/mm/aaaa também é aceito, porque é como o pessoal digita no Excel.
  const br = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  return texto;
};

class VisaoPessoas extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      aba: 'ferias',
      status: 'ativo',
      ano: new Date().getFullYear(),
      // 0 = todos os anos. Rescisão é evento raro, então o padrão é ver tudo.
      anoRescisao: 0,
      // Benefício e salário são por mês: o valor sai dos lançamentos, que
      // têm competência. null = deixa o servidor escolher o mês mais recente
      // com lançamento, porque abrir no mês corrente mostraria tela vazia
      // todo dia 1º. A lista de meses vem junto da consulta. As duas abas
      // compartilham o mês de propósito: quem está conferindo agosto quer
      // ver agosto nos dois lugares.
      competencia: null,
      competenciasDisponiveis: [],
      // Aba Benefícios tem duas páginas: 'vale_transporte' e 'alimentacao'
      // (pedidos do mês de VT e de vale-alimentação/refeição). A página que
      // somava tudo o que foi pago nos lançamentos saiu a pedido do RH.
      paginaBeneficios: 'vale_transporte',
      carregando: false,
      ferias: [],
      decimo: [],
      salarios: [],
      rescisao: [],
      fgts: [],
      // Janela de detalhe: mostra só o assunto da aba aberta, para quem clicou
      // em "férias" não cair na ficha inteira.
      detalheAberto: false,
      detalheCpf: '',
      detalheNome: '',
      detalheCarregando: false,
      detalheLinha: null,
      detalheLinhas: [],
      detalheExtra: [],
      // Importação
      importAberto: false,
      importTipo: null,
      importArquivo: '',
      importLinhas: [],
      importPrevia: null,
      importGravando: false,
      // Mudanças de salário aguardando um sócio (aba Salários).
      aprovacoesAberto: false,
      aprovacoes: [],
      aprovacoesPodeAprovar: false,
      aprovacoesDecidindo: false,
      aprovacoesRecusaId: null,
      aprovacoesRecusaMotivo: ''
    };

    this.inputArquivo = React.createRef();

    // Mesma cópia por coluna da listagem de funcionários: o navegador não deixa
    // selecionar uma coluna com o mouse, então cada cabeçalho ganha um botão.
    this.copia = criarCopiaDeColuna();
  }

  componentDidMount() {
    this.carregar();
  }

  carregar = () => {
    const { aba, status, ano, competencia } = this.state;
    // As páginas de pedido carregam sozinhas (ValeTransporte, ValeAlimentacao).
    if (aba === 'beneficios') return;
    this.setState({ carregando: true });

    const consulta =
      aba === 'ferias'
        ? visaoAPI.getVisaoFerias(status)
        : aba === 'decimo'
        ? visaoAPI.getVisaoDecimoTerceiro(ano, status)
        : aba === 'salarios'
        ? visaoAPI.getVisaoSalarios(status, competencia)
        : aba === 'rescisao'
        ? visaoAPI.getVisaoRescisao(this.state.anoRescisao || null)
        : visaoAPI.getVisaoFgts(status);

    consulta
      .then((r) => {
        const novo = { [aba]: (r && r.request) || [], carregando: false };
        // O servidor devolve qual mês respondeu e quais existem. Guardo os
        // dois para o select nascer preenchido e marcado no mês certo, sem
        // uma segunda chamada só para listar competências.
        if (aba === 'salarios' && r) {
          if (r.competencia) novo.competencia = r.competencia;
          if (r.competencias) novo.competenciasDisponiveis = r.competencias;
        }
        this.setState(novo);
        if (aba === 'salarios') this.carregarAprovacoes();
      })
      .catch((e) => {
        this.setState({ carregando: false });
        // Mostra a mensagem que o servidor mandou em vez de um texto genérico:
        // sem isso, um erro de SQL vira "não foi possível carregar" e some.
        const msg =
          (e && e.response && e.response.message) ||
          'Não foi possível carregar os dados.';
        toast.error(msg);
      });
  };

  trocarAba = (evento, aba) => {
    this.setState({ aba }, this.carregar);
  };

  pegarSexo = (evento) => {
    this.setState({ sexo: evento.target.value }, this.carregar);
  };

  trocarStatus = (evento) => {
    this.setState({ status: evento.target.value }, this.carregar);
  };

  trocarAno = (evento) => {
    this.setState({ ano: parseInt(evento.target.value, 10) }, this.carregar);
  };

  trocarPaginaBeneficios = (evento) => {
    this.setState({ paginaBeneficios: evento.target.value }, this.carregar);
  };

  trocarCompetencia = (evento) => {
    this.setState({ competencia: evento.target.value }, this.carregar);
  };

  trocarAnoRescisao = (evento) => {
    this.setState(
      { anoRescisao: parseInt(evento.target.value, 10) },
      this.carregar
    );
  };

  irParaFicha = (cpf) => {
    browserHistory.push(`${process.env.PUBLIC_URL}/funcionarios/${cpf}`);
  };

  // Abre a janela com o histórico daquela pessoa NAQUELE assunto. Benefícios
  // e FGTS não precisam de ida ao servidor: a linha da lista já traz tudo,
  // então a janela mostra a própria linha.
  abrirDetalhe = (linha) => {
    const { aba } = this.state;
    const cpf = linha.cpf_funcionario;

    this.setState({
      detalheAberto: true,
      detalheCpf: cpf,
      detalheNome: linha.nome_funcionario || '',
      // A própria linha da lista, para o resumo de cálculo da janela — evita
      // uma segunda consulta só para repetir números que já vieram.
      detalheLinha: linha,
      detalheLinhas: [],
      detalheExtra: [],
      detalheCarregando: aba !== 'fgts'
    });

    if (aba === 'fgts') {
      this.setState({ detalheLinhas: [linha] });
      return;
    }

    const consulta =
      aba === 'ferias'
        ? funcionarioAPI.getFerias(cpf)
        : aba === 'decimo'
        ? funcionarioAPI.getDecimoTerceiro(cpf)
        : aba === 'salarios'
        ? funcionarioAPI.getHistoricoSalarial(cpf)
        : funcionarioAPI.getDesligamento(cpf);

    consulta
      .then((r) => {
        this.setState({
          detalheCarregando: false,
          detalheLinhas: (r && r.request) || [],
          // Só o 13º traz uma segunda lista: os lançamentos do financeiro.
          detalheExtra: (r && r.financeiro) || []
        });
      })
      .catch((e) => {
        this.setState({ detalheCarregando: false });
        const msg =
          (e && e.response && e.response.message) ||
          'Não foi possível carregar o detalhe.';
        toast.error(msg);
      });
  };

  carregarAprovacoes = () => {
    funcionarioAPI
      .getSolicitacoesSalario('pendente')
      .then((r) =>
        this.setState({
          aprovacoes: (r && r.request) || [],
          aprovacoesPodeAprovar: !!(r && r.pode_aprovar)
        })
      )
      .catch(() => this.setState({ aprovacoes: [] }));
  };

  // Aprovar e recusar só sócio (o servidor confere); cancelar quem cadastra.
  decidirAprovacao = (pedido, decisao) => {
    const motivo = this.state.aprovacoesRecusaMotivo.trim();
    if (decisao === 'recusar' && !motivo) {
      toast.error('Informe o motivo da recusa.');
      return;
    }
    this.setState({ aprovacoesDecidindo: true });
    funcionarioAPI
      .decidirSalario(pedido.id, decisao, motivo)
      .then((r) => {
        toast.success((r && r.message) || 'Decisão registrada.');
        this.setState(
          { aprovacoesDecidindo: false, aprovacoesRecusaId: null, aprovacoesRecusaMotivo: '' },
          this.carregar
        );
      })
      .catch((e) => {
        this.setState({ aprovacoesDecidindo: false });
        toast.error(
          (e && e.response && e.response.message) ||
            'Não foi possível registrar a decisão.'
        );
      });
  };

  fecharDetalhe = () => {
    this.setState({
      detalheAberto: false,
      detalheCpf: '',
      detalheNome: '',
      detalheLinha: null,
      detalheLinhas: [],
      detalheExtra: []
    });
  };

  baixarModelo = (tipo) => {
    const modelo = MODELOS[tipo];
    const link = document.createElement('a');
    // ?v= evita o cache do navegador: o modelo é arquivo estático com nome
    // fixo, e sem isso quem já baixou uma vez continua recebendo a versão
    // antiga depois que o modelo muda.
    link.href = `${AppState.serverAddr}/arquivos/${modelo.arquivo}?v=${Date.now()}`;
    link.download = modelo.arquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  escolherArquivo = (tipo) => {
    this.setState({ importTipo: tipo }, () => {
      if (this.inputArquivo.current) {
        this.inputArquivo.current.value = '';
        this.inputArquivo.current.click();
      }
    });
  };

  arquivoSelecionado = (evento) => {
    const arquivo = evento.target.files && evento.target.files[0];
    if (!arquivo) return;

    const tipo = this.state.importTipo;
    const modelo = MODELOS[tipo];

    readXlsxFile(arquivo)
      .then((linhasBrutas) => {
        if (!linhasBrutas || linhasBrutas.length < 2) {
          toast.error('A planilha está vazia ou só tem o cabeçalho.');
          return;
        }

        // Mapeia pelo NOME da coluna e não pela posição: assim o DP pode
        // reordenar colunas na planilha sem quebrar a importação.
        const cabecalho = linhasBrutas[0].map((c) =>
          String(c || '').trim().toLowerCase()
        );
        const faltando = modelo.colunas.filter((c) => !cabecalho.includes(c));

        if (faltando.length > 0) {
          toast.error(
            `A planilha não tem a(s) coluna(s): ${faltando.join(', ')}. Baixe o modelo e use os mesmos cabeçalhos.`
          );
          return;
        }

        const indices = {};
        modelo.colunas.forEach((c) => {
          indices[c] = cabecalho.indexOf(c);
        });

        const camposData = modelo.colunas.filter(
          (c) => c.startsWith('dt_') || c.startsWith('periodo_')
        );

        const linhas = linhasBrutas
          .slice(1)
          .map((linha) => {
            const registro = {};
            modelo.colunas.forEach((coluna) => {
              const valor = linha[indices[coluna]];
              // Fórmula que devolve "" (linha ainda não preenchida, como as
              // colunas calculadas do modelo de férias) chega como NaN.
              const vazio =
                valor === null ||
                valor === undefined ||
                (typeof valor === 'number' && isNaN(valor));
              registro[coluna] = camposData.includes(coluna)
                ? vazio
                  ? ''
                  : paraISO(valor)
                : vazio
                ? ''
                : String(valor).trim();
            });
            return registro;
          })
          // Linha totalmente vazia no fim da planilha é lixo do Excel, não erro.
          .filter((registro) =>
            modelo.colunas.some((coluna) => registro[coluna] !== '')
          );

        if (linhas.length === 0) {
          toast.error('Nenhuma linha preenchida na planilha.');
          return;
        }

        const validar = IMPORTADOR[tipo](linhas, true);

        return validar.then((r) => {
          this.setState({
            importAberto: true,
            importArquivo: arquivo.name,
            importLinhas: linhas,
            importPrevia: (r && r.request) || null
          });
        });
      })
      .catch((e) => {
        const msg =
          (e && e.response && e.response.message) ||
          'Não foi possível ler a planilha.';
        toast.error(msg);
      });
  };

  confirmarImportacao = () => {
    const { importTipo, importLinhas } = this.state;
    this.setState({ importGravando: true });

    const gravar = IMPORTADOR[importTipo](importLinhas, false);

    gravar
      .then((r) => {
        const gravados = (r && r.request && r.request.gravados) || 0;
        this.setState(
          {
            importGravando: false,
            importAberto: false,
            importPrevia: null,
            importLinhas: []
          },
          this.carregar
        );
        toast.success(`${gravados} registro(s) importado(s) com sucesso.`);
      })
      .catch((e) => {
        this.setState({ importGravando: false });
        const msg =
          (e && e.response && e.response.message) ||
          'Não foi possível importar a planilha.';
        toast.error(msg);
      });
  };

  fecharImportacao = () => {
    this.setState({
      importAberto: false,
      importPrevia: null,
      importLinhas: [],
      importArquivo: ''
    });
  };

  colunaNome = () => ({
    Header: this.copia.cabecalho('Nome', 'nome_funcionario'),
    accessor: 'nome_funcionario',
    width: 240,
    Cell: (row) => (
      <span
        className="action-cell"
        style={{ color: '#2f6fa8', cursor: 'pointer' }}
        onClick={() => this.abrirDetalhe(row.original)}
      >
        {row.value}
      </span>
    )
  });

  colunasFerias = () => [
    this.colunaNome(),
    this.copia.coluna('CPF', 'cpf_funcionario', { width: 140 }),
    {
      Header: this.copia.cabecalho(
        'Situação',
        'situacao',
        (v) => ROTULO_SITUACAO[v] || v
      ),
      accessor: 'situacao',
      width: 130,
      Cell: (row) => (
        <Tag tone={TOM_SITUACAO[row.value] || 'grey'}>
          {ROTULO_SITUACAO[row.value] || row.value}
        </Tag>
      )
    },
    this.copia.coluna('Prazo', 'dt_limite', { width: 120, formatar: data }),
    this.copia.coluna('Dias restantes', 'dias_restantes', {
      width: 130,
      formatar: (v) => (v === null || v === undefined ? '—' : v)
    }),
    // Saldo é calculado a partir da admissão, não somado do que está
    // cadastrado — quem está anos em atraso tem direito acumulado mesmo sem
    // ninguém ter lançado os períodos.
    //
    // A aba mostra só o que o RH usa para AGIR: situação, prazo, dias
    // restantes e saldo. Direito acumulado, gozados, vendidos e avos são
    // memória de cálculo do passivo — ninguém marca férias olhando para eles,
    // e na lista só competiam por atenção. Foram para a janela de detalhe.
    this.copia.coluna('Dias de saldo', 'dias_saldo', { width: 120 }),
    this.copia.coluna('Última férias', 'ultima_ferias', {
      width: 130,
      formatar: data
    }),
    this.copia.coluna('Registros', 'registros', { width: 100 })
  ];

  colunasDecimo = () => [
    this.colunaNome(),
    this.copia.coluna('CPF', 'cpf_funcionario', { width: 140 }),
    this.copia.coluna('Adiantamento registrado', 'valor_adiantamento', {
      width: 200,
      formatar: dinheiro
    }),
    this.copia.coluna('Saldo registrado', 'valor_saldo', {
      width: 170,
      formatar: dinheiro
    }),
    this.copia.coluna('Lançado no financeiro', 'valor_lancado', {
      width: 190,
      formatar: dinheiro
    }),
    this.copia.coluna('Lançamentos', 'qtd_lancamentos', { width: 120 })
  ];

  colunasSalarios = () => [
    this.colunaNome(),
    this.copia.coluna('CPF', 'cpf_funcionario', { width: 140 }),
    this.copia.coluna('Salário na ficha', 'salario_ficha', {
      width: 170,
      formatar: dinheiro
    }),
    {
      Header: this.copia.cabecalho(
        'Salário vigente (histórico)',
        'salario_vigente',
        dinheiro
      ),
      accessor: 'salario_vigente',
      width: 230,
      Cell: (row) => (
        <span>
          {dinheiro(row.value)}
          {row.original.divergente ? (
            <span style={{ marginLeft: 6 }}>
              <Tag tone="orange">divergente</Tag>
            </span>
          ) : null}
          {row.original.salario_pendente ? (
            <span
              style={{ marginLeft: 6 }}
              title={`${dinheiro(row.original.salario_pendente)} a partir de ${data(row.original.dt_vigencia_pendente)}, aguardando aprovação`}
            >
              <Tag tone="blue">aguardando aprovação</Tag>
            </span>
          ) : null}
        </span>
      )
    },
    this.copia.coluna('Desde', 'dt_vigencia_inicio', {
      width: 120,
      formatar: data
    }),
    this.copia.coluna('Motivo', 'motivo', { width: 180 }),
    // Ajuda de custo e complemento como o histórico registra (o pago no mês
    // está nas colunas da direita).
    this.copia.coluna('Ajuda de custo (histórico)', 'ajuda_custo_vigente', {
      width: 210,
      formatar: dinheiro
    }),
    this.copia.coluna('Complemento (histórico)', 'complemento_vigente', {
      width: 200,
      formatar: dinheiro
    }),
    // Salário que já valia antes da aprovação por sócio não tem aprovador.
    {
      Header: this.copia.cabecalho('Aprovado por', 'aprovado_por_nome'),
      accessor: 'aprovado_por_nome',
      width: 190,
      Cell: (row) =>
        row.original.salario_vigente === null || row.original.salario_vigente === undefined
          ? '—'
          : row.value || 'Anterior à aprovação'
    },
    this.copia.coluna('Registros', 'registros', { width: 100 }),
    // O que de fato foi pago no mês escolhido, somado dos lançamentos. O
    // salário na empresa não é uma conta só: base + diária/ajuda de custo +
    // complemento. A base muda com o vínculo (CLT 2.1.1.1, estagiário 2.1.1.3,
    // sócio 2.1.1.6) e as três nunca aparecem juntas para a mesma pessoa, então
    // somam na mesma coluna.
    this.copia.coluna('Base do mês', 'base_mes', {
      width: 150,
      formatar: dinheiro
    }),
    this.copia.coluna('Diária e ajuda de custo', 'diaria_ajuda_mes', {
      width: 210,
      formatar: dinheiro
    }),
    this.copia.coluna('Complemento', 'complemento_mes', {
      width: 160,
      formatar: dinheiro
    }),
    this.copia.coluna('Total pago no mês', 'total_pago_mes', {
      width: 180,
      formatar: dinheiro
    }),
  ];

  colunasRescisao = () => [
    this.colunaNome(),
    this.copia.coluna('CPF', 'cpf_funcionario', { width: 140 }),
    this.copia.coluna('Desligamento', 'dt_desligamento', {
      width: 140,
      formatar: data
    }),
    {
      Header: this.copia.cabecalho('Motivo', 'motivo', (v) =>
        rotulo(ROTULO_MOTIVO, v)
      ),
      accessor: 'motivo',
      width: 190,
      Cell: (row) => (
        <Tag tone={row.value === 'justa_causa' ? 'red' : 'blue'} dot={false}>
          {rotulo(ROTULO_MOTIVO, row.value)}
        </Tag>
      )
    },
    this.copia.coluna('Aviso prévio', 'tipo_aviso_previo', {
      width: 140,
      formatar: (v) => rotulo(ROTULO_AVISO, v)
    }),
    this.copia.coluna('Dias de aviso', 'dias_aviso_previo', { width: 130 }),
    this.copia.coluna('Multa FGTS', 'multa_fgts_percentual', {
      width: 130,
      formatar: (v) =>
        v === null || v === undefined || v === '' ? '—' : `${v}%`
    }),
    this.copia.coluna('Admissão', 'dt_admissao', { width: 130, formatar: data }),
    this.copia.coluna('Tempo de casa', 'tempo_casa_anos', {
      width: 140,
      formatar: (v) =>
        v === null || v === undefined ? '—' : `${v} ano(s)`
    })
  ];

  // O saldo informado é o ponto de partida; o sistema só soma o que foi
  // lançado depois dele. Sem saldo, a linha fica marcada: é a fila do que
  // falta importar, e enquanto isso o passivo usa uma estimativa.
  colunasFgts = () => [
    this.colunaNome(),
    this.copia.coluna('CPF', 'cpf_funcionario', { width: 140 }),
    this.copia.coluna('Admissão', 'dt_admissao', { width: 120, formatar: data }),
    {
      Header: this.copia.cabecalho('Saldo informado', 'saldo_informado', dinheiro),
      accessor: 'saldo_informado',
      width: 190,
      Cell: (row) =>
        row.value === null || row.value === undefined ? (
          <Tag tone="grey">sem saldo</Tag>
        ) : (
          <span>{dinheiro(row.value)}</span>
        )
    },
    this.copia.coluna('Data do saldo', 'dt_saldo', { width: 130, formatar: data }),
    this.copia.coluna('Depósitos depois', 'depositos_apos_saldo', {
      width: 170,
      formatar: dinheiro
    }),
    this.copia.coluna('Saldo atual', 'saldo_atual', {
      width: 150,
      formatar: dinheiro
    }),
    this.copia.coluna('Multa 40%', 'multa_40', { width: 140, formatar: dinheiro }),
    this.copia.coluna('Depósitos no sistema', 'depositos_no_sistema', {
      width: 190,
      formatar: dinheiro
    }),
    this.copia.coluna('Último depósito', 'ultimo_deposito', {
      width: 140,
      formatar: data
    })
  ];

  // Colunas da janela de detalhe, por aba. Ficam juntas aqui porque é uma
  // tabelinha simples de leitura — não vale a pena passar pelo DataTable, que
  // traz busca, paginação e card branco que a janela não precisa.
  colunasDetalhe = () => {
    const { aba } = this.state;

    if (aba === 'ferias') {
      return [
        ['Período aquisitivo', (l) => `${data(l.periodo_aquisitivo_inicio)} a ${data(l.periodo_aquisitivo_fim)}`],
        ['Período concessivo', (l) => `${data(l.periodo_concessivo_inicio)} a ${data(l.periodo_concessivo_fim)}`],
        ['Período gozado', (l) => `${data(l.dt_gozo_inicio)} a ${data(l.dt_gozo_fim)}`],
        ['Dias gozados', (l) => l.dias_gozados],
        ['Dias de saldo', (l) => l.dias_saldo],
        ['Abono pecuniário', (l) => l.dias_vendidos]
      ];
    }

    if (aba === 'decimo') {
      return [
        ['Ano', (l) => l.ano],
        ['Adiantamento', (l) => dinheiro(l.valor_adiantamento)],
        ['Pago em', (l) => data(l.dt_pagamento_adiantamento)],
        ['Saldo', (l) => dinheiro(l.valor_saldo)],
        ['Pago em', (l) => data(l.dt_pagamento_saldo)],
        ['Rescisório', (l) => dinheiro(l.valor_rescisorio)]
      ];
    }

    if (aba === 'salarios') {
      return [
        ['Salário', (l) => dinheiro(l.salario)],
        ['Ajuda de custo', (l) => dinheiro(l.valor_ajuda_custo)],
        ['Complemento', (l) => dinheiro(l.valor_complemento)],
        ['Início da vigência', (l) => data(l.dt_vigencia_inicio)],
        ['Fim da vigência', (l) => data(l.dt_vigencia_fim)],
        ['Motivo', (l) => l.motivo || '—'],
        ['Aprovado por', (l) => l.aprovado_por_nome || (l.origem === 'importacao' ? 'Importação' : 'Anterior à aprovação')]
      ];
    }

    if (aba === 'fgts') {
      return [
        ['Saldo informado', (l) => dinheiro(l.saldo_informado)],
        ['Data do saldo', (l) => data(l.dt_saldo)],
        ['Depósitos depois do saldo', (l) => dinheiro(l.depositos_apos_saldo)],
        ['Saldo atual', (l) => dinheiro(l.saldo_atual)],
        ['Multa 40%', (l) => dinheiro(l.multa_40)],
        ['Depósitos no sistema (total)', (l) => dinheiro(l.depositos_no_sistema)]
      ];
    }

    if (aba === 'rescisao') {
      return [
        ['Desligamento', (l) => data(l.dt_desligamento)],
        ['Motivo', (l) => rotulo(ROTULO_MOTIVO, l.motivo)],
        ['Aviso prévio', (l) => rotulo(ROTULO_AVISO, l.tipo_aviso_previo)],
        ['Dias de aviso', (l) => l.dias_aviso_previo],
        ['Aviso de', (l) => `${data(l.dt_aviso_previo_inicio)} a ${data(l.dt_aviso_previo_fim)}`],
        ['Multa FGTS', (l) => (l.multa_fgts_percentual ? `${l.multa_fgts_percentual}%` : '—')]
      ];
    }

    return [];
  };

  render() {
    const {
      aba,
      status,
      ano,
      anoRescisao,
      competencia,
      competenciasDisponiveis,
      carregando,
      importAberto,
      importArquivo,
      importPrevia,
      importGravando,
      detalheAberto,
      detalheNome,
      detalheCarregando,
      detalheLinha,
      detalheLinhas,
      detalheExtra
    } = this.state;

    const dados = this.state[aba] || [];
    const colunas =
      aba === 'ferias'
        ? this.colunasFerias()
        : aba === 'decimo'
        ? this.colunasDecimo()
        : aba === 'salarios'
        ? this.colunasSalarios()
        : aba === 'rescisao'
        ? this.colunasRescisao()
        : this.colunasFgts();

    // Importação só existe onde ela resolve um problema real hoje: férias,
    // salário e o saldo de FGTS só existem em planilha ou extrato. 13º e
    // benefício já se alimentam dos lançamentos do financeiro (2.1.4.4 e
    // 2.1.3.x + 4.1.x).
    const tipoImportacao = MODELOS[aba] ? aba : null;

    const anoAtual = new Date().getFullYear();
    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 6; a--) {
      anos.push(a);
    }

    // Rescisão não usa o filtro de ativo/desligado: por definição só aparece
    // quem foi desligado. No lugar dele vai o ano, com "todos" como padrão.
    const filtros =
      aba === 'rescisao' ? (
        <FilterSelect value={anoRescisao} onChange={this.trocarAnoRescisao}>
          <MenuItem value={0}>Todos os anos</MenuItem>
          {anos.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </FilterSelect>
      ) : (
        <>
          <FilterSelect value={status} onChange={this.trocarStatus}>
            <MenuItem value="ativo">Ativo</MenuItem>
            <MenuItem value="inativo">Desligado</MenuItem>
            <MenuItem value="todos">Todos</MenuItem>
          </FilterSelect>
          {aba === 'decimo' ? (
            <FilterSelect value={ano} onChange={this.trocarAno}>
              {anos.map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </FilterSelect>
          ) : null}
          {/* Só os meses que têm lançamento daquele assunto — oferecer um
              mês vazio só geraria a dúvida de se a tela quebrou. */}
          {aba === 'salarios' &&
          competenciasDisponiveis.length > 0 ? (
            <FilterSelect
              value={competencia || competenciasDisponiveis[0]}
              onChange={this.trocarCompetencia}
            >
              {competenciasDisponiveis.map((c) => (
                <MenuItem key={c} value={c}>
                  {rotuloCompetencia(c)}
                </MenuItem>
              ))}
            </FilterSelect>
          ) : null}
        </>
      );

    // Seletor de página da aba Benefícios. Vai junto dos filtros nas duas
    // páginas, para dar para voltar de uma para a outra no mesmo lugar.
    const paginaVt = this.state.paginaBeneficios === 'vale_transporte';
    const paginaVa = this.state.paginaBeneficios === 'alimentacao';
    const seletorPaginaBeneficios = (
      <FilterSelect value={this.state.paginaBeneficios} onChange={this.trocarPaginaBeneficios}>
        <MenuItem value="vale_transporte">Vale-transporte</MenuItem>
        <MenuItem value="alimentacao">Alimentação e refeição</MenuItem>
      </FilterSelect>
    );
    const filtrosComPagina =
      aba === 'beneficios' ? (
        <>
          {seletorPaginaBeneficios}
          {filtros}
        </>
      ) : (
        filtros
      );

    const pendentes = this.state.aprovacoes.length;
    const acoes = tipoImportacao ? (
      <>
        {aba === 'salarios' ? (
          <DsButton
            intent={pendentes > 0 ? 'dark' : 'outlined'}
            onClick={() => this.setState({ aprovacoesAberto: true })}
          >
            {`Aprovações pendentes (${pendentes})`}
          </DsButton>
        ) : null}
        <DsButton
          intent="outlined"
          onClick={() => this.baixarModelo(tipoImportacao)}
        >
          Baixar modelo
        </DsButton>
        <DsButton
          intent="primary"
          onClick={() => this.escolherArquivo(tipoImportacao)}
        >
          Importar planilha
        </DsButton>
      </>
    ) : null;

    const previaAceitos = (importPrevia && importPrevia.aceitos) || [];
    const previaRecusados = (importPrevia && importPrevia.recusados) || [];

    return (
      <div id={this.constructor.name}>
        <PageTitle>Gestão</PageTitle>

        <Tabs
          value={aba}
          onChange={this.trocarAba}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value="ferias" label="Férias" />
          <Tab value="decimo" label="13º salário" />
          <Tab value="salarios" label="Salários" />
          <Tab value="beneficios" label="Benefícios" />
          <Tab value="rescisao" label="Rescisão" />
          <Tab value="fgts" label="FGTS" />
        </Tabs>

        <input
          type="file"
          accept=".xlsx"
          ref={this.inputArquivo}
          style={{ display: 'none' }}
          onChange={this.arquivoSelecionado}
        />

        {aba === 'beneficios' && paginaVt ? (
          <ValeTransporte seletorPagina={seletorPaginaBeneficios} />
        ) : aba === 'beneficios' && paginaVa ? (
          <ValeAlimentacao seletorPagina={seletorPaginaBeneficios} />
        ) : (
        <DataTable
          actions={acoes}
          filters={filtrosComPagina}
          data={dados}
          columns={colunas}
          className="ds-table -highlight ds-table--scroll-x"
          getTableProps={this.copia.capturarLinhasVisiveis}
          loading={carregando}
          defaultPageSize={20}
          showSearch
          showTotal
          searchPlaceholder="Buscar por nome ou CPF"
          noDataText="Nenhum funcionário encontrado"
        />
        )}

        <Modal
          isOpen={this.state.aprovacoesAberto}
          onClose={() =>
            this.setState({ aprovacoesAberto: false, aprovacoesRecusaId: null, aprovacoesRecusaMotivo: '' })
          }
          title="Mudanças de salário aguardando aprovação"
          maxWidth="lg"
          fullWidth
        >
          <p style={{ marginTop: 0, color: '#666' }}>
            {this.state.aprovacoesPodeAprovar
              ? 'Só sócios aprovam ou recusam. A mudança passa a valer quando aprovada.'
              : 'Só sócios aprovam ou recusam. Você pode cancelar um pedido feito por engano.'}
          </p>
          {this.state.aprovacoes.length === 0 ? (
            <p>Nenhuma mudança aguardando aprovação.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Nome', 'Salário atual', 'Novo salário', 'Ajuda de custo', 'Complemento', 'A partir de', 'Motivo', 'Pedido por', ''].map((t) => (
                      <th key={t} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {this.state.aprovacoes.map((p) => (
                    <tr key={p.id}>
                      <td style={{ padding: '6px 8px' }}>{p.nome_funcionario}</td>
                      <td style={{ padding: '6px 8px' }}>{dinheiro(p.salario_atual)}</td>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{dinheiro(p.salario)}</td>
                      <td style={{ padding: '6px 8px' }}>{dinheiro(p.valor_ajuda_custo)}</td>
                      <td style={{ padding: '6px 8px' }}>{dinheiro(p.valor_complemento)}</td>
                      <td style={{ padding: '6px 8px' }}>{data(p.dt_vigencia_inicio)}</td>
                      <td style={{ padding: '6px 8px' }}>{p.motivo}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {p.solicitado_por_nome || p.solicitado_por || '—'}
                        {p.origem === 'importacao' ? ' (importação)' : ''}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {this.state.aprovacoesRecusaId === p.id ? (
                          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <input
                              placeholder="Motivo da recusa"
                              value={this.state.aprovacoesRecusaMotivo}
                              onChange={(e) => this.setState({ aprovacoesRecusaMotivo: e.target.value.slice(0, 255) })}
                              style={{ padding: 6, minWidth: 200 }}
                            />
                            <DsButton intent="primary" disabled={this.state.aprovacoesDecidindo} onClick={() => this.decidirAprovacao(p, 'recusar')}>
                              Confirmar
                            </DsButton>
                            <DsButton intent="text" onClick={() => this.setState({ aprovacoesRecusaId: null })}>
                              Voltar
                            </DsButton>
                          </span>
                        ) : this.state.aprovacoesPodeAprovar ? (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <DsButton intent="primary" disabled={this.state.aprovacoesDecidindo} onClick={() => this.decidirAprovacao(p, 'aprovar')}>
                              Aprovar
                            </DsButton>
                            <DsButton intent="secondary" disabled={this.state.aprovacoesDecidindo} onClick={() => this.setState({ aprovacoesRecusaId: p.id, aprovacoesRecusaMotivo: '' })}>
                              Recusar
                            </DsButton>
                          </span>
                        ) : (
                          <DsButton intent="text" disabled={this.state.aprovacoesDecidindo} onClick={() => this.decidirAprovacao(p, 'cancelar')}>
                            Cancelar pedido
                          </DsButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>

        <Modal
          isOpen={importAberto}
          onClose={this.fecharImportacao}
          title="Conferir importação"
          maxWidth="md"
          fullWidth
        >
          <p>
            Arquivo <strong>{importArquivo}</strong>:{' '}
            <strong>{previaAceitos.length}</strong> linha(s) serão importadas e{' '}
            <strong>{previaRecusados.length}</strong> serão ignoradas.
          </p>

          {/* Salários: linha sem fim de vigência é o salário atual e vai para
              aprovação de um sócio em vez de entrar direto no histórico. */}
          {previaAceitos.filter((a) => a.destino === 'aprovacao').length > 0 ? (
            <p style={{ color: '#8a6d3b' }}>
              {previaAceitos.filter((a) => a.destino === 'aprovacao').length} linha(s) de salário
              atual começando a partir de 08/10/2026 vão para aprovação de um sócio e só valem
              depois de aprovadas.
            </p>
          ) : null}

          {/* FGTS: cada linha troca o saldo que a pessoa já tinha. Vale
              avisar antes de confirmar, porque não é soma. */}
          {previaAceitos.filter((a) => a.saldo_anterior !== undefined && a.saldo_anterior !== null).length > 0 ? (
            <p style={{ color: '#8a6d3b' }}>
              {previaAceitos.filter((a) => a.saldo_anterior !== undefined && a.saldo_anterior !== null).length}{' '}
              pessoa(s) já tinham saldo informado; o saldo novo substitui o anterior.
            </p>
          ) : null}

          {previaRecusados.length > 0 ? (
            <div style={{ maxHeight: 280, overflowY: 'auto', marginTop: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>
                      Linha
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>
                      CPF
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>
                      Motivo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previaRecusados.map((r) => (
                    <tr key={`${r.linha}-${r.cpf}`}>
                      <td style={{ padding: '4px 8px' }}>{r.linha}</td>
                      <td style={{ padding: '4px 8px' }}>{r.cpf || '—'}</td>
                      <td style={{ padding: '4px 8px' }}>{r.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div
            style={{
              marginTop: 16,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8
            }}
          >
            <DsButton intent="text" onClick={this.fecharImportacao}>
              Cancelar
            </DsButton>
            <DsButton
              intent="primary"
              disabled={importGravando || previaAceitos.length === 0}
              onClick={this.confirmarImportacao}
            >
              {importGravando
                ? 'Importando...'
                : `Importar ${previaAceitos.length} linha(s)`}
            </DsButton>
          </div>
        </Modal>

        <Modal
          isOpen={detalheAberto}
          onClose={this.fecharDetalhe}
          title={detalheNome || 'Detalhe'}
          maxWidth="md"
          fullWidth
        >
          <p style={{ marginTop: 0, color: '#666' }}>
            {TITULO_DETALHE[aba]}
          </p>

          {/* Memoria de calculo do passivo. Sai da lista porque o RH nao marca
              ferias olhando para estes numeros, mas continua a um clique de
              distancia de quem precisa conferir o saldo. Os valores vem da
              propria linha clicada — nao ha segunda consulta. */}
          {aba === 'ferias' && detalheLinha ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 24,
                padding: '12px 0 16px',
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                marginBottom: 12
              }}
            >
              {[
                ['Direito acumulado', `${numeroOuTraco(detalheLinha.dias_direito)} dia(s)`],
                ['Dias gozados (total)', numeroOuTraco(detalheLinha.dias_gozados)],
                ['Dias vendidos (total)', numeroOuTraco(detalheLinha.dias_vendidos)],
                ['Avos em curso', `${numeroOuTraco(detalheLinha.avos_periodo_atual)}/12`]
              ].map(([titulo, valor]) => (
                <div key={titulo}>
                  <div style={{ fontSize: 12, color: '#666' }}>{titulo}</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{valor}</div>
                </div>
              ))}
            </div>
          ) : null}

          {detalheCarregando ? (
            <p>Carregando...</p>
          ) : detalheLinhas.length === 0 ? (
            <p>Nenhum registro para esta pessoa.</p>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {this.colunasDetalhe().map(([titulo]) => (
                      <th
                        key={titulo}
                        style={{
                          textAlign: 'left',
                          padding: '6px 8px',
                          borderBottom: '1px solid rgba(0,0,0,0.12)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {titulo}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detalheLinhas.map((linha, indice) => (
                    <tr key={linha.id || indice}>
                      {this.colunasDetalhe().map(([titulo, valorDe]) => (
                        <td
                          key={titulo}
                          style={{
                            padding: '6px 8px',
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {valorDe(linha)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* O 13º é o único assunto com duas origens: o que o DP registrou e o
              que saiu no financeiro. Mostro as duas, separadas, porque conferir
              uma contra a outra é o motivo de abrir esta janela. */}
          {aba === 'decimo' && detalheExtra.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <strong>Lançamentos no financeiro (conta 2.1.4.4)</strong>
              <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Ano</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Pagamento</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Competência</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Valor</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Projeto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalheExtra.map((l) => (
                      <tr key={l.id_lancamento}>
                        <td style={{ padding: '6px 8px' }}>{l.ano}</td>
                        <td style={{ padding: '6px 8px' }}>{data(l.dt_pag_recb)}</td>
                        <td style={{ padding: '6px 8px' }}>{l.referencia || '—'}</td>
                        <td style={{ padding: '6px 8px' }}>{dinheiro(l.valor)}</td>
                        <td style={{ padding: '6px 8px' }}>{l.desc_curta || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div
            style={{
              marginTop: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            {/* Esta janela é só consulta. Quem precisa lançar ou corrigir vai
                para a ficha, onde está o contexto todo da pessoa. */}
            <DsButton
              intent="text"
              onClick={() => this.irParaFicha(this.state.detalheCpf)}
            >
              Abrir ficha completa
            </DsButton>
            <DsButton intent="primary" onClick={this.fecharDetalhe}>
              Fechar
            </DsButton>
          </div>
        </Modal>
      </div>
    );
  }
}

export default VisaoPessoas;
